/**
 * 外链直跳（带 app_token，不调 userinfo）
 *
 * 告警详情：/#/alarm/detail/{id}?app_token&project_id&projectName&userRole
 * 时序图全屏：/#/sensor/chart-fullscreen?app_token&project_id&sn&fieldKey
 * 设备详情：/#/sensor/detail/{sn}?app_token&project_id
 * 添加设备：/#/sensor/add?app_token&project_id
 */
import { getToken, setToken } from '@/utils'
import { buildHostContext, normalizeChannelNumber } from '@/utils/host'

/** 告警详情路由名 */
export const ALARM_DETAIL_ROUTE_NAME = 'alarm-detail'

/** 设备时序图全屏路由名 */
export const SENSOR_CHART_FULLSCREEN_ROUTE_NAME = 'sensor-chart-fullscreen'

/** 设备详情路由名 */
export const SENSOR_DETAIL_ROUTE_NAME = 'sensor-detail'

/** 添加设备路由名 */
export const SENSOR_ADD_ROUTE_NAME = 'sensor-add'

/**
 * 外链直进必填字段。每条路由单独声明，没有默认分支。
 * app_token 由 isExternalEntryFromUrl 判断，不写在表里。
 */
export const EXTERNAL_ENTRY_RULES = {
  [ALARM_DETAIL_ROUTE_NAME]: { query: ['project_id'], params: ['id'] },
  [SENSOR_CHART_FULLSCREEN_ROUTE_NAME]: {
    query: ['project_id', 'sn', 'fieldKey'],
  },
  [SENSOR_DETAIL_ROUTE_NAME]: { query: ['project_id'], params: ['sn'] },
  [SENSOR_ADD_ROUTE_NAME]: { query: ['project_id'] },
}

function hasEntryValue(value) {
  return value != null && String(value) !== ''
}

/** URL 是否携带 app_token 且目标为外链直进页 */
export function isExternalEntryFromUrl(to) {
  return !!(to.query?.app_token && EXTERNAL_ENTRY_RULES[to.name])
}

/** 当前路由的必填 query / params 是否齐全（token 已由 syncTokenFromUrl 写入） */
export function hasExternalEntryParams(to) {
  const rule = EXTERNAL_ENTRY_RULES[to.name]
  if (!rule || !getToken()) return false
  const queryOk = (rule.query || []).every((key) => hasEntryValue(to.query?.[key]))
  const paramsOk = (rule.params || []).every((key) => hasEntryValue(to.params?.[key]))
  return queryOk && paramsOk
}

/** 当前会话是否由外链进入（守卫处理 app_token 后写入 store） */
export function isExternalEntry() {
  return !!window.$store?.isExternalEntry
}

/** app_token 写入 localStorage */
export function syncTokenFromUrl(to) {
  const appToken = to.query?.app_token
  if (appToken) {
    setToken(appToken)
    window.$store.isApp = true
  }
}

/** project_id + projectName / project_name 写入 currentProject（不调接口） */
export function syncProjectFromUrl(to) {
  const projectId = to.query?.project_id || to.params?.project_id
  if (!projectId) return

  const projectName =
    to.query?.projectName ||
    to.query?.project_name ||
    window.$store.currentProject?.name ||
    ''

  window.$store.currentProject = {
    id: String(projectId),
    name: String(projectName),
  }
}

/** query userRole 写入 userInfo.sys_role（不调 userinfo 接口） */
export function syncUserRoleFromExternalUrl(to) {
  const userRole =
    to.query?.userRole || to.query?.user_role || to.query?.sys_role || ''
  if (!userRole) return

  window.$store.userInfo = {
    ...window.$store.userInfo,
    sys_role: String(userRole),
    id: window.$store.userInfo?.id || 'external',
  }
}

/** 标记当前会话为外链进入 */
export function markExternalEntry() {
  window.$store.isExternalEntry = true
  window.$store.isApp = true
}

/**
 * App 事件 payload 同步上下文（project_id / project_name / app_token）
 * @returns {boolean} 是否更新了上下文
 */
export function syncAppContextFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return false

  const projectId = payload.project_id
  const projectName = payload.project_name ?? payload.projectName
  const appToken = payload.app_token

  if (!projectId && !projectName && !appToken) return false

  if (appToken) {
    setToken(appToken)
    window.$store.isApp = true
  }
  if (projectId || projectName != null) {
    window.$store.currentProject = {
      id: projectId
        ? String(projectId)
        : String(window.$store.currentProject?.id || ''),
      name:
        projectName != null
          ? String(projectName)
          : String(window.$store.currentProject?.name || ''),
    }
  }
  return true
}

/**
 * 组装 App 原生告警详情参数（字段与外链 URL 一致）
 * path: alarm_id（H5 为 /alarm/detail/:id）
 * query: app_token, project_id, projectName, userRole
 */
export function buildAppAlarmDetailPayload(row) {
  if (!row) return null
  return buildHostContext({ alarm_id: String(row.id) })
}

/** 外链 / App 内嵌：跳转原生告警详情 */
export function goToAppAlarmDetail(row) {
  const payload = buildAppAlarmDetailPayload(row)
  if (!payload) return
  window.AIOTBridge?.goToDetail?.(payload)
}

/**
 * 组装 App 原生设备详情参数（与 App sensor-web-bridge 一致）
 * Bridge 仍走 goToDetail，字段为 sn / channel_number（非 alarm_id）
 */
export function buildAppSensorDetailPayload(row) {
  if (!row?.sn) return null
  return buildHostContext({
    sn: String(row.sn),
    channel_number: String(normalizeChannelNumber(row.channel_number)),
    title: row.name || row.sn || '设备详情',
    password: row.remote_config?.password || '',
  })
}

/** App 内嵌：跳转原生设备详情（另一 web-view） */
export function goToAppSensorDetail(row) {
  const payload = buildAppSensorDetailPayload(row)
  if (!payload) return
  window.AIOTBridge?.goToDetail?.(payload)
}

/**
 * 组装 App 时序图全屏参数：app_token + project_id + 业务 query
 * @param {Record<string, string>} chartQuery buildSensorChartFullscreenQuery 的返回值
 */
export function buildAppSensorChartFullscreenPayload(chartQuery = {}) {
  const ctx = buildHostContext()
  if (!chartQuery?.sn || !chartQuery?.fieldKey || !ctx.project_id) return null
  return {
    app_token: ctx.app_token,
    project_id: ctx.project_id,
    ...chartQuery,
  }
}

/** App 内嵌：另开 web-view 打开时序图全屏 */
export function goToAppSensorChartFullscreen(chartQuery) {
  const payload = buildAppSensorChartFullscreenPayload(chartQuery)
  if (!payload) return
  window.AIOTBridge?.goToChartFullscreen?.(payload)
}

/**
 * 组装 App 添加设备参数（与 App sensor-web-bridge pickSensorAddContext 一致）
 */
export function buildAppSensorAddPayload() {
  const ctx = buildHostContext()
  if (!ctx.project_id) return null
  return {
    project_id: ctx.project_id,
    projectName: ctx.projectName,
    app_token: ctx.app_token,
  }
}

/** App 内嵌：另开 web-view 打开添加设备 */
export function goToAppSensorAdd() {
  const payload = buildAppSensorAddPayload()
  if (!payload) return
  window.AIOTBridge?.goToAdd?.(payload)
}
